import { Directive } from '@angular/core';
import { IconComponent, IconFunction } from '@ey-xd/ng-motif';
import { MOTIF_ICON_FUNCTION_MAP, normalizeIconPath } from './motif-icon-function-map';

// `<motif-icon [src]>` resolves icons by fetching the given path over HTTP. The new
// motif version no longer ships those SVG assets, so mapped legacy paths are rendered
// through the new icon functions instead. Unmapped paths (app-local SVGs) keep the
// original fetch behavior. The host icon method is intercepted in the constructor,
// which runs before the component's first `ngOnChanges`, so no HTTP request is made
// for mapped icons and there is no dependency on directive/component hook ordering.
//
// Icon functions write a `fill` attribute onto the generated <svg>/<path>. That defeats
// existing styling that colors icons through `svg { fill }` (e.g. status dots). The fill
// attributes are stripped so the rendered SVG behaves like the previously served files,
// letting CSS / `currentColor` drive the icon color as before.
@Directive({
  selector: 'motif-icon[src]',
  standalone: false,
})
export class MotifIconSrcDirective {
  constructor(private readonly icon: IconComponent) {
    const originalGenerateFromSrc = this.icon.generateIconFromSrc.bind(this.icon);

    this.icon.generateIconFromSrc = (path: string): void => {
      if (!path) {
        // Reset state so a previously rendered icon is cleared when [src] becomes
        // empty/undefined (e.g. conditional rendering), instead of leaving it stale.
        this.icon.iconFunction = undefined;
        this.icon.svg = '';
        return;
      }
      const iconFunction = MOTIF_ICON_FUNCTION_MAP[normalizeIconPath(path)];
      if (iconFunction) {
        if (!this.icon.size) {
          this.icon.size = '24';
        }
        const strippedIconFunction: IconFunction = (options) => {
          const element = iconFunction(options);
          element.removeAttribute('fill');
          element
            .querySelectorAll('[fill]')
            .forEach((node) => node.removeAttribute('fill'));
          return element;
        };
        this.icon.iconFunction = strippedIconFunction;
        this.icon.generateIconFromFunction(strippedIconFunction);
        return;
      }
      this.icon.iconFunction = undefined;
      originalGenerateFromSrc(path);
    };
  }
}
